/* eslint-disable consistent-return */
import i18n from 'i18next';
import { VehicleType } from '../../../resources/base/models/vehicleTypes';
import { Yup } from '../../../services/common/validation';
import { regexVin } from '../../../services/common/validationUtils';
import { ConfigurationsSection } from '../../../types/vehicleConfig';

const vinLength = {
  min: 17,
};

const regexBattery = /^(?=.*\d)[\d.,\s]{1,120}$/;
const regexNumericSpaces = /^\d{1,3}(\d{3})*$/;
const regexLicencePlate = /^[A-Za-z]{2}-\d{3}-[A-Za-z]{2}$/;

export const VehicleConfigurationFinancialSchema = (demoCarCheckbox?, expandedSection?, vehicleType?) => {
  if (expandedSection === ConfigurationsSection.BASECONFIGURATION) {
    const initialForm = Yup.object().shape({
      brand: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
      model: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
      modelGroup: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
      engine: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
      modelName: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
      vehicleMilesId: Yup.string().when('vehicleType', {
        is: (vehicleType) => vehicleType === VehicleType.USED || vehicleType === VehicleType.STOCK,
        then: (schema) => schema.required(i18n.t('step1:configVehicle:mandatoryField')),
        otherwise: (schema) => schema.notRequired(),
      }),
      modelDescription: Yup.string().when('vehicleType', {
        is: (value) => value === VehicleType.USED,
        then: (schema) => schema.required(i18n.t('step1:configVehicle:mandatoryField')),
        otherwise: (schema) => schema.notRequired(),
      }),
      // TODO: review if this is actually required or not
      vin: Yup.string().when('vehicleType', {
        is: (value) => value === VehicleType.STOCK || value === VehicleType.USED,
        then: (schema) =>
          schema
            .required(i18n.t('step1:configVehicle:mandatoryField'))
            .matches(regexVin, i18n.t('validation:invalidCharacter'))
            .test(
              'is-length-17',
              i18n.t('validation:minLength', { value: vinLength.min }),
              (value) => value?.length === vinLength.min
            ),
      }),
      licensePlate: Yup.string().when('vehicleType', {
        is: (value) => value === VehicleType.USED,
        then: (schema) =>
          schema
            .matches(regexLicencePlate, i18n.t('validation:invalidCharacter'))
            .min(7, i18n.t('validation:minLength', { value: 9 }))
            .max(9, i18n.t('validation:maxLength', { value: 9 })),
      }),
      co2emision: Yup.string()
        .required(i18n.t('step1:configVehicle:mandatoryField'))
        .min(1, i18n.t('validation:minLength', { value: 1 }))
        .max(3, i18n.t('validation:maxLength', { value: 3 })),
      batteryPrice: Yup.string().when('engine', {
        is: (value) => ['Electric', 'Hybrid'].includes(value),
        then: (schema) =>
          schema
            .required(i18n.t('step1:configVehicle:mandatoryField'))
            .matches(regexBattery, i18n.t('validation:invalidCharacter')),
      }),
    });
    const demoNewCarValidationSchema = Yup.object().shape({
      currMileage: Yup.string()
        .required(i18n.t('step1:configVehicle:mandatoryField'))
        .matches(regexNumericSpaces, i18n.t('validation:invalidCharacter')),
    });
    const demoCarValidationSchema = Yup.object().shape({
      regDate: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
      currMileage: Yup.string()
        .required(i18n.t('step1:configVehicle:mandatoryField'))
        .matches(regexNumericSpaces, i18n.t('validation:invalidCharacter')),
    });
    const newCarValidationSchema = Yup.object().shape({
      catalogPriceBto: Yup.string()
        .required(i18n.t('step1:configVehicle:mandatoryField'))
        .min(0, i18n.t('step1:configVehicle:invalidNumberField')),
      co2emision: Yup.string()
        .required(i18n.t('step1:configVehicle:mandatoryField'))
        .min(1, i18n.t('validation:minLength', { value: 1 }))
        .max(3, i18n.t('validation:maxLength', { value: 3 })),
      fiscalPower: Yup.string()
        .required(i18n.t('step1:configVehicle:mandatoryField'))
        .min(1, i18n.t('validation:minLength', { value: 1 }))
        .max(128, i18n.t('validation:maxLength', { value: 128 })),
    });
    if (demoCarCheckbox && vehicleType === VehicleType.STOCK) {
      return initialForm.concat(demoCarValidationSchema);
    }
    if (demoCarCheckbox && vehicleType === VehicleType.NEW) {
      return initialForm.concat(demoNewCarValidationSchema);
    }
    if (vehicleType === VehicleType.USED) {
      return initialForm.concat(demoCarValidationSchema);
    }
    if (vehicleType === VehicleType.NEW) {
      return initialForm.concat(newCarValidationSchema);
    }
    return initialForm;
  }
  if (vehicleType === VehicleType.USED) {
    const initialForm = Yup.object().shape({
      estDelivery: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
      catalogPrice: Yup.string()
        .required(i18n.t('step1:configVehicle:mandatoryField'))
        .min(4, i18n.t('validation:minLength', { value: 4 }))
        .max(120, i18n.t('validation:maxLength', { value: 120 })),
      fiscalPower: Yup.number().required(i18n.t('step1:configVehicle:mandatoryField')),
      isRimSelected: Yup.boolean(),
      rims: Yup.string().when('isRimSelected', {
        is: true,
        then: (schema) => schema.required(i18n.t('step1:configVehicle:mandatoryField')),
      }),
      department: Yup.string().when('regCost', {
        is: (value) => value === true,
        then: (schema) => schema.required(i18n.t('validation:required')),
      }),
    });
    return initialForm;
  }
  if (vehicleType === VehicleType.STOCK) {
    const initialForm = Yup.object().shape({
      fiscalPower: Yup.number().required(i18n.t('step1:configVehicle:mandatoryField')),
      department: Yup.string().when('regCost', {
        is: (value) => value === true,
        then: (schema) => schema.required(i18n.t('step1:configVehicle:mandatoryField')),
      }),
    });
    return initialForm;
  }
  if (vehicleType === VehicleType.NEW) {
    const initialForm = Yup.object().shape({
      department: Yup.string().when('regCost', {
        is: (value) => value === true,
        then: (schema) => schema.required(i18n.t('step1:configVehicle:mandatoryField')),
      }),
    });
    return initialForm;
  }
};

export const VehicleConfigurationStandaloneSchema = (vehicleType, demoCarCheckbox?) => {
  const initialForm = Yup.object().shape({
    brand: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
    model: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
    modelGroup: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
    engine: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
    modelName: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
    catalogPrice: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
    currMileage: Yup.string().matches(regexNumericSpaces, i18n.t('validation:invalidCharacter')),
  });
  const schemaForUsedAndDemo = Yup.object().shape({
    regDate: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
    currMileage: Yup.string()
      .required(i18n.t('step1:configVehicle:mandatoryField'))
      .matches(regexNumericSpaces, i18n.t('validation:invalidCharacter')),
    vin: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
  });
  const schemaForStock = Yup.object().shape({
    estDelivery: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
    vin: Yup.string().required(i18n.t('step1:configVehicle:mandatoryField')),
  });
  if (demoCarCheckbox && vehicleType === VehicleType.USED) {
    return initialForm.concat(schemaForUsedAndDemo);
  }
  if (demoCarCheckbox && (vehicleType === VehicleType.STOCK || vehicleType === VehicleType.NEW)) {
    return initialForm.concat(schemaForStock);
  }
  return initialForm;
};
